import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { inject } from '@app/providers/QAU.ContextProvider';
import { isTabletMode } from '@app/utils/utils';
import LocalizedText from '@LocalizedText';
import {
  setWhiteInkOptionsNextDisabled,
  setIncludesWhiteUnderprint,
  setWhiteInkOption,
  setTolerance,
  setAdvancedModeVisible,
  setTouchUpMode,
} from '@app/actions/whiteInkOptionsActions';
import './WhiteInkOptionsPanel.scss';
import Dropdown from '@components/dropdown/Dropdown';
import Slider from '@components/slider/Slider';
import Button from '@components/buttons/Button';
import AdvancedMode from './AdvancedMode/AdvancedMode';

const WhiteInkOptionsPanel = ({
  flowConfig,
  context,
  setWhiteInkOptionsNextDisabled,
  setIncludesWhiteUnderprint,
  includesWhiteUnderprint,
  setWhiteInkOption,
  whiteInkOption,
  setTolerance,
  tolerance,
  advancedModeVisible,
  setAdvancedModeVisible,
  setTouchUpMode,
  advancedModeProps,
  toleranceSliderEnabled,
  isTabletMode,
  resetButtonDisabled,
  resetAllChanges,
}) => {
  const isWhiteUnderprintChangeAllowed = flowConfig.features.featureExistsAndEnabled('whiteUnderprintOptions');
  const [minTolerance, setMinTolerance] = useState(0);
  const [maxTolerance, setMaxTolerance] = useState(20);
  const { quote = {} } = context.projectData || {};
  const paperMaterial = context.getPaperMaterial();
  const underprintCheckboxLocKey = paperMaterial.blendingColor
    ? 'qau-includes-underprint-checkbox-text'
    : 'qau-includes-underprint-checkbox-no-blending-text';

  const removeWhiteOptions = [
    {
      value: 'keepAllWhite',
    },
    {
      value: 'removeAllWhite',
    },
    {
      value: 'removeWhiteBackground',
    },
    ...(isTabletMode ? [] : [{ value: 'selectWhiteToRemove' }]),
  ];

  const selectedWhiteOption = useMemo(() => {
    return removeWhiteOptions.find(option => option.value === whiteInkOption);
  }, [whiteInkOption]);

  const onIncludeUnderprintChange = ({ target: { checked } }) => {
    setIncludesWhiteUnderprint(checked);
  };

  const onChangeRemoveWhiteType = selectedOption => {
    resetAllChanges();
    setWhiteInkOption(selectedOption.value);
    setTouchUpMode(null);
    setAdvancedModeVisible(false);
  };

  const onAdvancedModeToggle = () => {
    setTouchUpMode(null);
    setAdvancedModeVisible(!advancedModeVisible);
  };

  useEffect(() => {
    const removeWhiteFeature = flowConfig.features.getFeature('imageRemoveWhite');
    const max = +removeWhiteFeature.getParamValue('maxTolerance');
    const min = +removeWhiteFeature.getParamValue('minTolerance');
    const defaultValue = tolerance || +removeWhiteFeature.getParamValue('defaultTolerance');

    setMinTolerance(min);
    setMaxTolerance(max);
    setTolerance(defaultValue);
    whiteInkOption && setWhiteInkOption(whiteInkOption);
  }, [flowConfig, setTolerance, setWhiteInkOption, tolerance, whiteInkOption]);

  useEffect(() => {
    if (!quote) return;

    setIncludesWhiteUnderprint(quote.includesWhiteUnderprint === 'Y');
  }, []);

  useEffect(() => {
    const {
      projectData: { quote },
    } = context;
    setWhiteInkOptionsNextDisabled(includesWhiteUnderprint && !whiteInkOption);

    if (!quote) return;

    quote.includesWhiteUnderprint = includesWhiteUnderprint ? 'Y' : 'N';
  }, [includesWhiteUnderprint, whiteInkOption]);

  const onChangeTolerance = value => {
    setTolerance(value);
  };

  return (
    <div id="qau-white-options-panel">
      {isWhiteUnderprintChangeAllowed && (
          <div className="qau-white-options-wrapper">
            <LocalizedText id="qau-white-options-panel-title-text" tagName="span" />
            <div className="includes-underprint">
              <label htmlFor="includes-underprint-checkbox">
                <input
                    type="checkbox"
                    id="includes-underprint-checkbox"
                    onChange={onIncludeUnderprintChange}
                    checked={includesWhiteUnderprint}
                />
                <span><LocalizedText id={underprintCheckboxLocKey} tagName="span" /></span>
              </label>
            </div>
          </div>
      )}
      <div className={classnames('qau-white-adjustments-wrapper', {disabled: !includesWhiteUnderprint})}>
        <LocalizedText id="qau-white-adjustments-panel-text" tagName="span" />
        <div className="dropdown-wrapper">
          <Dropdown
              options={removeWhiteOptions}
              onSelect={onChangeRemoveWhiteType}
              selected={selectedWhiteOption}
          />
        </div>
        {['removeAllWhite', 'removeWhiteBackground'].includes(whiteInkOption) &&
          <div className="qau-white-adjustment-tools-wrapper">
            <div className="tolerance-slider-wrapper">
              <LocalizedText id="qau-white-tolerance-title-text" tagName="span" />
              <Slider
                step={1}
                defaultValue={tolerance}
                minValue={minTolerance}
                maxValue={maxTolerance}
                onFinishChanging={onChangeTolerance}
                onChangeValueByStep={onChangeTolerance}
                minValueLocaleKey="qau-white-tolerance-less-title-text"
                maxValueLocaleKey="qau-white-tolerance-more-title-text"
                disabled={!toleranceSliderEnabled}
              />
            </div>
            {!isTabletMode &&
              <div className="qau-advanced-mode-wrapper">
                <Button className="qau-advanced-mode-toggle-btn" onClick={onAdvancedModeToggle}>
                  <LocalizedText id={`qau-white-options-advanced-mode-${advancedModeVisible ? 'hide' : 'show'}-text`} tagName="span" />
                </Button>
                {advancedModeVisible && <AdvancedMode {...advancedModeProps} />}
              </div>
            }
          </div>
        }
        {whiteInkOption === 'selectWhiteToRemove' &&
          <Button className="qau-reset-btn" onClick={resetAllChanges} disabled={resetButtonDisabled}>
            <LocalizedText id="qau-reset-btn" tagName="b" />
          </Button>
        }
      </div>
    </div>
  );
};

const mapStateToProps = (
  { matrix,
    whiteInkOptions: {
      includesWhiteUnderprint,
      whiteInkOption,
      tolerance,
      advancedMode: {
        isVisible: advancedModeVisible,
        toleranceSliderEnabled: advancedModeToleranceSliderEnabled,
      }
    },
  }, {
    flowConfig,
    context,
  }) => {
    const { ctx } = matrix;
  const toleranceSliderEnabled = !advancedModeVisible || !advancedModeToleranceSliderEnabled;
  return {
    flowConfig,
    context,
    includesWhiteUnderprint,
    whiteInkOption,
    tolerance,
    advancedModeVisible,
    toleranceSliderEnabled,
    isTabletMode: isTabletMode(ctx),
  };
};

const mapDispatchToProps = {
  setWhiteInkOptionsNextDisabled,
  setIncludesWhiteUnderprint,
  setWhiteInkOption,
  setTolerance,
  setAdvancedModeVisible,
  setTouchUpMode,
};

WhiteInkOptionsPanel.propTypes = {
  flowConfig: PropTypes.object.isRequired,
  context: PropTypes.object.isRequired,
  advancedModeProps: PropTypes.object.isRequired,
  toleranceSliderEnabled: PropTypes.bool,
  resetButtonDisabled: PropTypes.bool.isRequired,
  resetAllChanges: PropTypes.func.isRequired,
};

export default inject('flowConfig', 'context')(connect(mapStateToProps, mapDispatchToProps)(WhiteInkOptionsPanel));
